import { CvpLocale } from "../constants/CvpLocale";
import { useSettings } from "../settings/useSettings";
import { CVPSearchParams, queryStringStore } from "../url/wsQueryStringStore";
import { useWhoAmI } from "../user/useWhoAmI";
import { lp } from "../utils/logPrefix";
import { getStorageArea } from "../utils/storageArea";
import { validateLocale } from "./validateLocale";

const storageArea = getStorageArea("session");

/**
 *
 * This is the only place where locale is determined
 *
 * LOCALE PRIORITY:
 * 1. settings?.iframe_overrideLocale
 * 2. locale fra auth/WhoAmI    (oppdatert med authAPI.saveLocaleChoice)
 * 3. locale fra querystring (f.eks &cvplocale=en)
 * 4. locale fra sessionStorage.getItem("locale")
 * 5. locale fra settings (velges i /admin)
 * 6. fallback til nb
 *
 * 2 AND 3 ARE THE OTHER WAY AROUND FROM WHAT THEY WERE, and that is the whole
 * point of the list being here rather than inlined. `?cvplocale` used to
 * outrank the signed-in user's own saved choice, which was harmless while the
 * only thing producing that parameter was the admin preview. Simplified signing
 * now puts it on the invitation link: the consultant picks a language when they
 * send, and it travels in the link so the signing pages open in the language
 * the invitation was written in. For someone who is NOT signed in, that is
 * exactly right and the only signal there is. For someone who is, it would mean
 * a consultant's guess about a person silently overriding what that person has
 * told us themselves — so the profile wins, and the link is the fallback.
 *
 * A signer who disagrees with either still has the dropdown on the signing
 * page; `useSetLocale` strips `cvplocale` when it saves, so an explicit choice
 * outranks both without needing a rung of its own.
 *
 * THE ONE EXCEPTION is the admin translation preview, which depends on the old
 * order: it loads CVp in an iframe with `?cvplocale=<the language being edited>`
 * while the admin is signed in with a profile locale of their own. Under the new
 * order their profile would win and the preview would render the wrong language
 * — so inside that preview, and only there, the query string still comes first.
 * It is recognised by the `isAdminPreview` parameter the sandbox already puts on
 * that URL. Not by `isAdminPreview()`, which reads an attribute off
 * `window.frameElement`: that answers «am I inside some iframe», and an embedded
 * CVp always is.
 *
 * See also useSetLocale()
 *
 */
export function useEvaluatedLocale() {
  return useEvaluatedLocaleWithSource().locale;
}

/**
 * The same answer, plus WHERE it came from.
 *
 * `isFallback` is true when not one of the sources above had anything to say
 * and the `nb` at the bottom of the list is all there is. That is a different
 * situation from a deliberate Norwegian, and one caller needs to tell them
 * apart: the simplified-signing page. The server resolves its own language from
 * the invitee's profile when the page does not state one — an invitation link
 * sent before `cvplocale` was put on it, say — and reports it back on the view.
 * The page may then adopt it, but only if it had nothing of its own: adopting
 * over an explicit choice would take the picker away from the signer.
 *
 * Everything else should keep calling `useEvaluatedLocale`.
 */
export function useEvaluatedLocaleWithSource(): {
  locale: CvpLocale;
  isFallback: boolean;
} {
  const settings = useSettings();
  const { whoAmI } = useWhoAmI();
  const localeFromQs =
    queryStringStore.qs[CVPSearchParams.cvplocale]?.toString();
  const isAdminTranslationPreview =
    queryStringStore.qs[CVPSearchParams.isAdminPreview]?.toString() === "true";

  // The sources in priority order, so that the order IS the list and cannot
  // drift from the comment above it.
  const sources: { name: string; value: string | undefined | null }[] = [
    {
      name: "settings?.iframe_overrideLocale",
      value: settings?.iframe_overrideLocale,
    },
    ...(isAdminTranslationPreview
      ? [
          { name: "query string (admin preview)", value: localeFromQs },
          { name: "WhoAmI", value: whoAmI.locale },
        ]
      : [
          { name: "WhoAmI", value: whoAmI.locale },
          { name: "query string", value: localeFromQs },
        ]),
    { name: "sessionStorage", value: storageArea.getItem("locale") },
    { name: "settings?.locale", value: settings?.locale },
  ];

  for (const source of sources) {
    if (!source.value) continue;

    if (validateLocale(source.value))
      return { locale: source.value as CvpLocale, isFallback: false };

    // A source that is set but nonsense is logged and skipped, not returned —
    // same as before. The next source down decides.
    console.error(lp(), `Invalid locale from ${source.name}`, source.value);
  }

  // fallback til nb
  return { locale: CvpLocale.nb, isFallback: true };
}
